import jwt_decode from 'jwt-decode';
import { Keyset } from '@unipasswallet/keys';
import api from '@/service/backend';
import blockchain, { genUnipassWalletContext } from '@/service/blockchain';
import { addSignCapabilityToKeyset, updateKeyset } from '@/utils/rbac';
import Tss from '@/utils/tss';
import { MainExecuteTransaction, RawMainExecuteCall, Wallet } from '@unipasswallet/wallet';
import { CancelLockKeysetHashTxBuilder } from '@unipasswallet/transaction-builders';
import { useUserStore } from '@/store/user';
import { RpcRelayer } from '@unipasswallet/relayer';
import { sdkConfig } from '@/service/chains-config';
import { getOAuthUserInfo } from './storages';
import { safeDecryptKeyStore, safeEncryptKeystore } from '@/utils/oauth/keystore';
import { ElMessageBox } from 'element-plus';
import i18n from '@/plugins/i18n';
import { encryptSessionKey } from '@/utils/session-key';
import DB from './index-db';
import { clearUpSignToken } from '@/utils/oauth/check-up-sign-token';
import { OAuthProvider, AuditStatus, SignType } from '@/service/enum';
const {
  t: $t
} = i18n.global;
export const useRecoveryStore = defineStore({
  id: 'recovery',
  state: () => {
    return {
      email: '',
      token: '',
      password: '',
      confirmPassword: '',
      step: 1,
      oauthSignToken: '',
      oauth_provider: OAuthProvider.GOOGLE,
      loading: false,
      canSendStartRecoveryTx: false,
      isHaveTimeLock: false,
      verificationEmailHashs: [],
      the2FA: {
        token: '',
        type: 0,
        show: false,
        verify: ''
      }
    };
  },
  actions: {
    async restoreFromOAuthSign(oauthFromState, idToken) {
      var _getOAuthUserInfo;

      const userStore = useUserStore();
      const state = JSON.parse(oauthFromState);
      this.$state = state;
      const account_info = await DB.getAccountInfo(true);
      if (!account_info) return;
      await userStore.update(account_info);
      if (!idToken) return;
      const {
        sub: newSub
      } = jwt_decode(idToken);

      if (newSub !== ((_getOAuthUserInfo = getOAuthUserInfo()) === null || _getOAuthUserInfo === void 0 ? void 0 : _getOAuthUserInfo.sub)) {
        this.oauthSignToken = idToken;
        ElMessageBox.confirm($t('GoogleTip', {
          data: account_info.email
        }), $t('GoogleTitle'), {
          dangerouslyUseHTMLString: true,
          confirmButtonText: $t('TryAgain'),
          cancelButtonText: $t('Cancel')
        }).then(() => {// todo try again
        }).catch(() => {});
        return;
      }

      this.oauthSignToken = idToken;
      const {
        keyset
      } = account_info;

      const _keyset = Keyset.fromJson(keyset.keysetJson);

      const emailOptionsOrEmailHash = _keyset.keys[1].emailOptionsOrEmailHash;
      const registerEmailHash = typeof emailOptionsOrEmailHash === 'string' ? emailOptionsOrEmailHash : emailOptionsOrEmailHash.emailHash;
      this.verificationEmailHashs.push(registerEmailHash);
      const res = await api.sendRecoveryOAuthSig({
        verificationEmailHash: registerEmailHash,
        newMasterKeyAddress: keyset.masterKeyAddress,
        idToken
      });

      if (res.ok) {}
    },

    async uploadCloudKey() {
      const oauthUserInfo = getOAuthUserInfo();
      if (!oauthUserInfo || !oauthUserInfo.unipass_info) return;
      const {
        id_token,
        expires_at,
        oauth_provider,
        unipass_info
      } = oauthUserInfo;
      const {
        keyset,
        address,
        keyType
      } = unipass_info;
      const decoded = jwt_decode(id_token);
      const {
        email
      } = decoded;
      const action = 'sendRecoveryEmail';
      const localKeyData = await Tss.generateLocalKey({
        email: email,
        action
      });
      if (!localKeyData) return;
      const encryptedKeystore = await safeEncryptKeystore(localKeyData.keystore, this.password);
      localKeyData.keystore = encryptedKeystore;
      const {
        encrypted_key,
        aes_key
      } = await encryptSessionKey(localKeyData.keystore);
      this.email = email;
      const masterKeyAddress = localKeyData.localKeyAddress;
      const newKeyset = updateKeyset(keyset, masterKeyAddress);
      const resToken = await api.uploadRecoveryMasterKey({
        masterKey: {
          masterKeyAddress,
          keyStore: encryptedKeystore
        }
      });

      if (!resToken.ok) {
        return;
      }

      const user = {
        email: this.email,
        id_token,
        address,
        user_key: {
          encrypted_key,
          aes_key
        },
        oauth_provider,
        expires_at,
        keyset: {
          hash: newKeyset.hash(),
          masterKeyAddress,
          keysetJson: newKeyset.obscure().toJson(),
          keyType
        },
        keystore: localKeyData.keystore
      };
      const userStore = useUserStore();
      await userStore.update(user);
      this.step = 2;
    },

    async sendCancelRecovery(password) {
      const accountInfo = await DB.getAccountInfo();
      if (!accountInfo) return;
      const {
        email,
        address,
        keyset: _keyset
      } = accountInfo;
      const metaNonce = await blockchain.getMetaNonce(address);
      const txBuilder = new CancelLockKeysetHashTxBuilder(address, metaNonce, true, '0x');
      const digestHash = txBuilder.digestMessage();
      const content = txBuilder.build();
      const originKey = await safeDecryptKeyStore(accountInfo.keystore, password);
      const auditRes = await api.audit({
        type: SignType.PersonalSign,
        content,
        msg: digestHash
      });

      if (auditRes.ok) {
        clearUpSignToken();
      }

      if (auditRes.ok && auditRes.data.approveStatus === AuditStatus.Approved) {
        const signature = await Tss.generateTssTxSignature(originKey, _keyset.masterKeyAddress, digestHash);
        const keyset = await addSignCapabilityToKeyset(_keyset.keysetJson, () => Promise.resolve(signature));
        const relayer = new RpcRelayer(sdkConfig.urlConfig.relayer.polygon, genUnipassWalletContext(), blockchain.getProvider());
        const keyWallet = new Wallet({
          address,
          keyset,
          provider: blockchain.getProvider(),
          relayer: relayer
        });
        const nonce = await keyWallet.getNonce();
        const {
          chainId
        } = await blockchain.getProvider().getNetwork();
        const tx = (await txBuilder.generateSignature(keyWallet, [0])).build();
        const rawExecuteCall = new RawMainExecuteCall(tx, nonce, []);
        const {
          execute
        } = await keyWallet.signRawMainExecuteCall(rawExecuteCall, chainId, nonce);
        const {
          callType,
          gasLimit,
          target,
          data
        } = new MainExecuteTransaction({
          executeCall: execute,
          target: keyWallet.address
        }).toTransaction();
        const cancelResData = await api.cancelRecovery({
          email,
          metaNonce,
          signature: txBuilder.signature,
          transaction: {
            callType: callType,
            gasLimit: gasLimit.toHexString(),
            target: target,
            value: gasLimit.toHexString(),
            data: data
          }
        });
        return cancelResData.ok;
      }

      return false;
    }

  }
});